import type { Arc, Status } from './arc';
import { innerArcNames, isInFlight, STATUSES } from './arc';

/** A file the sync minted beside its original because two devices wrote the
 *  same path. Real files as far as the vault is concerned, noise as far as
 *  the board is concerned — counted, never rendered as arcs. */
export function isConflictSibling(path: string): boolean {
  return path.includes('.sync-conflict');
}

export interface BoardRow {
  arc: Arc;
  /** 0 at top level; deeper inside a storyline. */
  depth: number;
  /**
   * A holder's head repeated over members that sorted into another column —
   * furniture, not an occurrence of the arc. The arc itself renders exactly
   * once, in its own status's column; this is what keeps its members grouped
   * once they leave it.
   */
  repeat?: true;
}

export interface BoardColumn {
  status: Status;
  rows: BoardRow[];
}

export interface Board {
  columns: BoardColumn[];
  /**
   * Everything in play, as ONE ordered run — the band the desk actually draws.
   *
   * Not the `needs-input` and `active` columns concatenated: that puts every asking arc ahead of
   * every active one, which is the split this is here to avoid, and gives a holder with members in
   * both a repeated head in each. Built in top-level order instead, so the two interleave exactly
   * as they would if `needs-input` had never been a separate status.
   */
  inFlight: BoardRow[];
  conflictCount: number;
  /** Arcs held by some storyline — excluded from top level, since a grouped
   *  arc shows inside its storyline OR at top level, never both. */
  groupedNames: Set<string>;
}

/**
 * Every arc lands in exactly one place — inside its storyline if it has one,
 * at top level otherwise.
 *
 * "Otherwise" has to include a case the data can reach and the app cannot:
 * a hand-edited cycle. If A holds B and B holds A, both are grouped, so
 * excluding grouped arcs from top level would render NEITHER and the pair
 * would silently disappear. Membership is therefore decided by what the walk
 * actually reaches, not by what claims to hold what.
 */
export function buildBoard(arcs: Arc[], conflictCount: number): Board {
  const byName = new Map(arcs.map((arc) => [arc.name, arc]));

  const claimed = new Set<string>();
  for (const arc of arcs) {
    for (const inner of innerArcNames(arc)) {
      if (byName.has(inner)) claimed.add(inner);
    }
  }

  const roots = arcs.filter((arc) => !claimed.has(arc.name));
  const rendered = new Set(roots.map((arc) => arc.name));
  const nested = new Map<string, BoardRow[]>();
  for (const root of roots) {
    const rows = expand(root, byName);
    nested.set(root.name, rows);
    for (const row of rows) rendered.add(row.arc.name);
  }

  // Claimed by something that is itself unreachable — promote rather than
  // lose it. An orphaned arc is a data problem to see, not to hide.
  const orphans = arcs.filter((arc) => !rendered.has(arc.name));
  for (const orphan of orphans) rendered.add(orphan.name);

  const tops = [...roots, ...orphans].sort(byOrderThenTitle);
  const columns: BoardColumn[] = STATUSES.map((status) => ({ status, rows: [] }));
  const columnFor = (status: Status) => columns.find((c) => c.status === status)!;
  const inFlight: BoardRow[] = [];
  // Written to both the status column and the in-flight run, so the band and a status-filtered
  // view never disagree about what a holder holds.
  const emit = (status: Status, rows: BoardRow[]) => {
    columnFor(status).rows.push(...rows);
    if (isInFlight(status)) inFlight.push(...rows);
  };

  for (const top of tops) {
    const rows = nested.get(top.name) ?? [];
    emit(top.status, [{ arc: top, depth: 0 }]);

    // A holder that is NOT in flight keeps its whole subtree with it. A done
    // storyline showing the work it left behind is a reading; a next or parked
    // one has not started, so its members are not queued behind anything.
    if (!isInFlight(top.status) || rows.length === 0) {
      emit(top.status, rows);
      continue;
    }

    // An in-flight holder keeps its in-flight members — they belong to the same band, so moving
    // them would only put this holder's head in that band twice. What leaves is what belongs
    // somewhere else, under a repeat of the head, because the point of moving it is that it stays
    // grouped.
    const kids = subtrees(rows);
    emit(top.status, kids.filter((s) => isInFlight(s[0]!.arc.status)).flatMap(rebase));
    for (const [status, moved] of byStatus(kids.filter((s) => !isInFlight(s[0]!.arc.status)))) {
      emit(status, [{ arc: top, depth: 0, repeat: true }, ...moved]);
    }
  }

  return { columns, inFlight, conflictCount, groupedNames: claimed };
}

/** A child's subtree re-based to sit directly under the holder it is rendered with. */
function rebase(subtree: BoardRow[]): BoardRow[] {
  const child = subtree[0]!;
  return subtree.map((row) => ({ arc: row.arc, depth: row.depth - child.depth + 1 }));
}

/** A holder's direct children, each with its own descendants trailing it. */
function subtrees(rows: BoardRow[]): BoardRow[][] {
  const out: BoardRow[][] = [];
  for (const row of rows) {
    if (row.depth === 1) out.push([row]);
    else out[out.length - 1]?.push(row);
  }
  return out;
}

/**
 * Direct children bucketed by their own status, in the order they were held.
 *
 * A child's own subtree travels with it and is re-based to sit under it, so a
 * storyline moved into another column still renders as a holder there rather
 * than spilling its members onto the grid beside it.
 */
function byStatus(children: BoardRow[][]): Map<Status, BoardRow[]> {
  const out = new Map<Status, BoardRow[]>();
  for (const subtree of children) {
    const child = subtree[0]!;
    out.set(child.arc.status, [...(out.get(child.arc.status) ?? []), ...rebase(subtree)]);
  }
  return out;
}

/**
 * A storyline's arcs, flattened with their depth. Nesting is unbounded, so
 * this walks; `seen` is the safety net, because the app refuses a cycle at
 * the write but a hand-edited file has no such guard and must not hang the
 * board.
 */
function expand(arc: Arc, byName: Map<string, Arc>, seen = new Set([arc.name]), depth = 1): BoardRow[] {
  const rows: BoardRow[] = [];
  for (const name of innerArcNames(arc)) {
    const inner = byName.get(name);
    if (!inner || seen.has(name)) continue;
    seen.add(name);
    rows.push({ arc: inner, depth });
    rows.push(...expand(inner, byName, seen, depth + 1));
  }
  return rows;
}

function byOrderThenTitle(a: Arc, b: Arc): number {
  return a.order - b.order || a.title.localeCompare(b.title);
}
